import { CalendarClock, Timer, Hourglass, Gavel, Send } from 'lucide-react';
import { formatDate } from '@/lib/utils';

/** "3d 4h" / "2h 15m" / "12m" between two instants. */
export function formatDuration(fromIso: string, toIso?: string | null): string {
  const from = new Date(fromIso).getTime();
  const to = toIso ? new Date(toIso).getTime() : Date.now();
  const mins = Math.max(0, Math.floor((to - from) / 60000));
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  const rem = mins % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${rem}m`;
  return `${rem}m`;
}

interface CaseTimelineProps {
  createdAt?: string | null;
  timerExpiresAt?: string | null;
  decidedAt?: string | null;
  submittedAt?: string | null;
  /** 'pending' shows a live "pending for" duration instead of a closed one. */
  status?: string | null;
}

/** The case's own clock — when it arrived, its portal timer, and how long it
 *  has been (or was) waiting. Shared by the approval and history detail views. */
export function CaseTimeline({
  createdAt,
  timerExpiresAt,
  decidedAt,
  submittedAt,
  status,
}: CaseTimelineProps) {
  if (!createdAt) return null;
  const pending = status === 'pending';
  const rows: { icon: typeof Timer; label: string; value: string; accent?: string }[] = [
    { icon: CalendarClock, label: 'Received', value: formatDate(createdAt) },
  ];
  if (timerExpiresAt) {
    rows.push({ icon: Timer, label: 'Portal Timer (T0)', value: formatDate(timerExpiresAt) });
  }
  rows.push({
    icon: Hourglass,
    label: pending ? 'Pending For' : 'Was Pending For',
    value: formatDuration(createdAt, pending ? null : (decidedAt ?? timerExpiresAt)),
    accent: pending ? 'text-amber-600' : undefined,
  });
  if (decidedAt) {
    rows.push({ icon: Gavel, label: 'Decided', value: formatDate(decidedAt) });
  }
  if (submittedAt) {
    rows.push({ icon: Send, label: 'Submitted to GlobeMed', value: formatDate(submittedAt) });
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
        Case Timeline
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        {rows.map((row) => (
          <div key={row.label}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <row.icon className="w-3.5 h-3.5 text-muted-foreground" />
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                {row.label}
              </p>
            </div>
            <p className={`text-sm font-medium ${row.accent ?? 'text-foreground'}`}>
              {row.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
